// Start-a-U.S.-company public wizard strings — EN + TR.
//
// Public surface at /start-company (no auth, no shell). Default language
// derives from navigator.language; a visible TR/EN switcher writes useLocaleStore.
//
// COMPLIANCE INVARIANTS (do not soften): Pexify coordinates administrative
// filing only — NO legal/tax advice, NO guaranteed approval/EIN, NO same-day /
// exact-timeline promise, NO invented price. Price is shown ONLY from the
// backend config / prepare-payment response.

import type { LocalizedDict } from '../index'

type StartCompanyStrings = {
  hero: {
    title: string
    subtitle: string
    disclaimer: string
    benefits: { title: string; body: string }[]
    cta: string
    resumeNotice: string
  }
  lang: { label: string; en: string; tr: string }
  nav: { back: string; next: string; step: (n: number, total: number) => string }
  common: {
    optional: string
    required: string
    yes: string
    no: string
    notSure: string
    saving: string
    loading: string
    errorTitle: string
    retry: string
    edit: string
    add: string
    remove: string
  }
  steps: { contact: string; company: string; parties: string; ein: string; review: string; payment: string }
  contact: {
    heading: string
    fullName: string
    email: string
    phone: string
    preferredLanguage: string
    country: string
    emailInvalid: string
    nameRequired: string
  }
  company: {
    heading: string
    nameChoicesLabel: string
    nameChoicesHelp: string
    name1: string
    name2: string
    name3: string
    dba: string
    entityType: string
    entityOptions: Record<'single_member_llc' | 'multi_member_llc' | 'c_corp' | 'other_unsure', string>
    preferredState: string
    stateGuidance: string
    category: string
    activityDescription: string
    activityHelp: string
    effectiveDate: string
    effectiveAsap: string
    effectiveFuture: string
    effectiveDateField: string
    mailingStatus: string
    mailingHave: string
    mailingNeed: string
    mailingAddress: string
    physicalAddress: string
    county: string
    registeredAgent: string
    agentHave: string
    agentNeed: string
    agentNotSure: string
    management: string
    memberManaged: string
    managerManaged: string
    name1Required: string
  }
  parties: {
    heading: string
    rpName: string
    rpTitle: string
    rpTitleHelp: string
    rpAddress: string
    taxIdType: string
    taxSsn: string
    taxItin: string
    taxForeign: string
    taxIdValue: string
    taxIdHelp: string
    foreignNote: string
    ownersHeading: string
    ownersHelp: string
    ownerName: string
    ownerAddress: string
    ownerPct: string
    addOwner: string
    numberOfMembers: string
    ownershipOver100: string
    rpNameRequired: string
  }
  ein: {
    heading: string
    intro: string
    reason: string
    reasonOptions: Record<
      'started_new_business' | 'hired_employees' | 'banking' | 'changed_org_type' | 'purchased_business' | 'other',
      string
    >
    dateStarted: string
    dateTbd: string
    closingMonth: string
    employeesHeading: string
    employeesHelp: string
    empAgricultural: string
    empHousehold: string
    empOther: string
    form944: string
    form944Help: string
    firstWages: string
    principalProducts: string
    previousEin: string
    previousEinNumber: string
    thirdParty: string
    thirdPartyHelp: string
    certification: string
    certificationHelp: string
  }
  review: {
    heading: string
    intro: string
    sectionContact: string
    sectionCompany: string
    sectionParties: string
    sectionEin: string
    editSection: string
    included: string
    notIncluded: string
    notIncludedBody: string
    amountDue: string
    priceUnavailable: string
    mustCertify: string
    mustAgree: string
    continueToPayment: string
  }
  payment: {
    heading: string
    intro: string
    payButton: string
    processing: string
    cardNote: string
    prepareError: string
    confirming: string
    slowTitle: string
    slowBody: (ref: string) => string
    successTitle: string
    successBody: (ref: string) => string
    referenceLabel: string
    failedTitle: string
    failedBody: string
  }
  months: string[]
}

const EN: StartCompanyStrings = {
  hero: {
    title: 'Start your U.S. company formation in one place.',
    subtitle:
      'Submit your information securely, complete payment, and let Pexify coordinate the formation process with its filing partner.',
    disclaimer:
      'Pexify provides administrative filing coordination and does not provide legal or tax advice.',
    benefits: [
      {
        title: 'Guided application',
        body: 'A step-by-step form gathers everything the filing partner needs — no guesswork.',
      },
      {
        title: 'Secure Stripe payment',
        body: 'Card details go straight to Stripe. Pexify never sees or stores your card.',
      },
      {
        title: 'Organized document delivery',
        body: 'Your formation file is tracked in one place from submission through completion.',
      },
    ],
    cta: 'Start my application',
    resumeNotice: 'We found a saved application on this device — resuming where you left off.',
  },
  lang: { label: 'Language', en: 'English', tr: 'Türkçe' },
  nav: {
    back: 'Back',
    next: 'Continue',
    step: (n, total) => `Step ${n} of ${total}`,
  },
  common: {
    optional: 'optional',
    required: 'required',
    yes: 'Yes',
    no: 'No',
    notSure: "I'm not sure",
    saving: 'Saving…',
    loading: 'Loading…',
    errorTitle: 'Something went wrong',
    retry: 'Try again',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
  },
  steps: {
    contact: 'Contact',
    company: 'Company',
    parties: 'Ownership',
    ein: 'EIN / SS-4',
    review: 'Review',
    payment: 'Payment',
  },
  contact: {
    heading: 'How can we reach you?',
    fullName: 'Full name',
    email: 'Email',
    phone: 'Phone',
    preferredLanguage: 'Preferred language',
    country: 'Country of residence',
    emailInvalid: 'Enter a valid email address.',
    nameRequired: 'Full name is required.',
  },
  company: {
    heading: 'Tell us about the company',
    nameChoicesLabel: 'Company name choices',
    nameChoicesHelp:
      'Give up to three choices in order of preference. Availability is confirmed by the filing partner.',
    name1: 'First choice',
    name2: 'Second choice',
    name3: 'Third choice',
    dba: 'Trade name / DBA',
    entityType: 'Entity type',
    entityOptions: {
      single_member_llc: 'Single-member LLC',
      multi_member_llc: 'Multi-member LLC',
      c_corp: 'C-Corporation',
      other_unsure: "Other / I'm not sure",
    },
    preferredState: 'Preferred state of formation',
    stateGuidance: "I'm not sure; I need guidance choosing a state",
    category: 'Principal business activity category',
    activityDescription: 'Describe your business activity',
    activityHelp: 'What does the company do, make, or sell?',
    effectiveDate: 'Desired effective date',
    effectiveAsap: 'As soon as possible',
    effectiveFuture: 'A specific future date',
    effectiveDateField: 'Effective date',
    mailingStatus: 'Company mailing address',
    mailingHave: 'I have a mailing address',
    mailingNeed: 'I need assistance with a mailing address',
    mailingAddress: 'Mailing address',
    physicalAddress: 'Principal physical address',
    county: 'County and state',
    registeredAgent: 'Registered agent',
    agentHave: 'I have a registered agent',
    agentNeed: 'I need assistance',
    agentNotSure: "I'm not sure",
    management: 'Management structure',
    memberManaged: 'Member-managed',
    managerManaged: 'Manager-managed',
    name1Required: 'At least a first company name choice is required.',
  },
  parties: {
    heading: 'Responsible party & owners',
    rpName: 'Responsible party name',
    rpTitle: 'Title / relationship to company',
    rpTitleHelp: 'e.g. Owner, Managing Member, President',
    rpAddress: 'Responsible party address',
    taxIdType: 'Tax ID type',
    taxSsn: 'U.S. SSN',
    taxItin: 'ITIN',
    taxForeign: 'Foreign / none',
    taxIdValue: 'SSN or ITIN',
    taxIdHelp: 'Your identifier is encrypted and is never shown back in full.',
    foreignNote:
      'No U.S. identifier is required for a foreign responsible party. The filing partner will guide EIN issuance.',
    ownersHeading: 'Owners / members',
    ownersHelp: 'Add each owner. Ownership percentages, if provided, should total 100%.',
    ownerName: 'Full name',
    ownerAddress: 'Address',
    ownerPct: 'Ownership %',
    addOwner: 'Add owner',
    numberOfMembers: 'Number of members',
    ownershipOver100: 'Total ownership exceeds 100%. Please review the percentages.',
    rpNameRequired: 'Responsible party name is required.',
  },
  ein: {
    heading: 'EIN application details (Form SS-4)',
    intro: 'These answers help the filing partner prepare the EIN application.',
    reason: 'Reason for applying',
    reasonOptions: {
      started_new_business: 'Started a new business',
      hired_employees: 'Hired employees',
      banking: 'Banking purposes',
      changed_org_type: 'Changed type of organization',
      purchased_business: 'Purchased a going business',
      other: 'Other',
    },
    dateStarted: 'Date business started',
    dateTbd: 'To be determined after formation',
    closingMonth: 'Closing month of accounting year',
    employeesHeading: 'Employees expected in the next 12 months',
    employeesHelp: 'Enter 0 if you do not expect to hire in that category.',
    empAgricultural: 'Agricultural',
    empHousehold: 'Household',
    empOther: 'Other',
    form944: 'Do you expect annual employment tax liability of $1,000 or less (Form 944)?',
    form944Help: 'Applies to smaller employers; the filing partner confirms eligibility.',
    firstWages: 'Date first wages were or will be paid',
    principalProducts: 'Principal line of merchandise, products, or services',
    previousEin: 'Has this entity applied for an EIN before?',
    previousEinNumber: 'Previous EIN',
    thirdParty: 'Authorize Pexify’s filing partner as third-party designee for this application',
    thirdPartyHelp:
      'This lets the filing partner speak with the IRS about this EIN application on your behalf.',
    certification:
      'I certify that the information provided is true and correct to the best of my knowledge.',
    certificationHelp: 'Required to submit your application.',
  },
  review: {
    heading: 'Review your application',
    intro: 'Please confirm everything is correct before payment. You can edit any section.',
    sectionContact: 'Contact',
    sectionCompany: 'Company',
    sectionParties: 'Ownership',
    sectionEin: 'EIN / SS-4',
    editSection: 'Edit',
    included: "What's included",
    notIncluded: 'What is not included',
    notIncludedBody:
      'This service covers administrative filing coordination only. It does not include legal or tax advice, guaranteed approval, or a guaranteed EIN issuance date.',
    amountDue: 'Amount due',
    priceUnavailable: 'The price will be confirmed on the payment step.',
    mustCertify: 'Please confirm the certification to continue.',
    mustAgree: 'Please authorize the designee to continue.',
    continueToPayment: 'Continue to payment',
  },
  payment: {
    heading: 'Payment',
    intro: 'Complete your secure payment to submit the application.',
    payButton: 'Pay & Submit Application',
    processing: 'Processing…',
    cardNote: 'Card details are handled by Stripe — they never touch Pexify.',
    prepareError: 'We could not start the payment. Please try again.',
    confirming: 'Confirming your payment…',
    slowTitle: 'Your payment is being confirmed',
    slowBody: (ref) =>
      `This is taking a little longer than usual. Your payment was submitted and your application (${ref}) is recorded — you can safely close this page and we will confirm by email. No need to pay again.`,
    successTitle: 'Application submitted',
    successBody: (ref) =>
      `Thank you. Your application ${ref} has been received and payment confirmed. Pexify will coordinate the next steps with its filing partner and keep you updated by email.`,
    referenceLabel: 'Your reference',
    failedTitle: 'Payment not completed',
    failedBody:
      'Your payment could not be confirmed. No application was submitted. Please try again or use a different card.',
  },
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
}

const TR: StartCompanyStrings = {
  hero: {
    title: "ABD'de şirket kurma sürecinizi tek yerden başlatın.",
    subtitle:
      'Bilgilerinizi güvenli biçimde iletin, ödemenizi tamamlayın ve kuruluş dosyanızın Pexify ekibi ile başvuru partneri arasındaki sürecini düzenli şekilde yönetin.',
    disclaimer:
      'Pexify idari başvuru koordinasyonu sağlar; hukuki veya vergi danışmanlığı vermez.',
    benefits: [
      {
        title: 'Rehberli başvuru',
        body: 'Adım adım form, başvuru partnerinin ihtiyaç duyduğu her şeyi toplar — tahmine yer yok.',
      },
      {
        title: 'Güvenli Stripe ödemesi',
        body: 'Kart bilgileri doğrudan Stripe’a gider. Pexify kartınızı görmez ve saklamaz.',
      },
      {
        title: 'Düzenli belge teslimi',
        body: 'Kuruluş dosyanız başvurudan tamamlanmaya kadar tek yerden takip edilir.',
      },
    ],
    cta: 'Başvurumu başlat',
    resumeNotice: 'Bu cihazda kayıtlı bir başvuru bulduk — kaldığınız yerden devam ediyoruz.',
  },
  lang: { label: 'Dil', en: 'English', tr: 'Türkçe' },
  nav: {
    back: 'Geri',
    next: 'Devam',
    step: (n, total) => `Adım ${n} / ${total}`,
  },
  common: {
    optional: 'isteğe bağlı',
    required: 'zorunlu',
    yes: 'Evet',
    no: 'Hayır',
    notSure: 'Emin değilim',
    saving: 'Kaydediliyor…',
    loading: 'Yükleniyor…',
    errorTitle: 'Bir şeyler ters gitti',
    retry: 'Tekrar dene',
    edit: 'Düzenle',
    add: 'Ekle',
    remove: 'Kaldır',
  },
  steps: {
    contact: 'İletişim',
    company: 'Şirket',
    parties: 'Ortaklık',
    ein: 'EIN / SS-4',
    review: 'Özet',
    payment: 'Ödeme',
  },
  contact: {
    heading: 'Size nasıl ulaşabiliriz?',
    fullName: 'Ad soyad',
    email: 'E-posta',
    phone: 'Telefon',
    preferredLanguage: 'Tercih edilen dil',
    country: 'İkamet ülkesi',
    emailInvalid: 'Geçerli bir e-posta adresi girin.',
    nameRequired: 'Ad soyad zorunludur.',
  },
  company: {
    heading: 'Şirket hakkında bilgi verin',
    nameChoicesLabel: 'Şirket adı tercihleri',
    nameChoicesHelp:
      'Tercih sırasına göre en fazla üç isim verin. Uygunluk başvuru partneri tarafından teyit edilir.',
    name1: 'Birinci tercih',
    name2: 'İkinci tercih',
    name3: 'Üçüncü tercih',
    dba: 'Ticari ad / DBA',
    entityType: 'Şirket türü',
    entityOptions: {
      single_member_llc: 'Tek üyeli LLC',
      multi_member_llc: 'Çok üyeli LLC',
      c_corp: 'C-Corporation',
      other_unsure: 'Diğer / Emin değilim',
    },
    preferredState: 'Tercih edilen kuruluş eyaleti',
    stateGuidance: 'Emin değilim; eyalet seçimi için rehberlik istiyorum',
    category: 'Ana faaliyet kategorisi',
    activityDescription: 'Faaliyetinizi açıklayın',
    activityHelp: 'Şirket ne yapar, üretir veya satar?',
    effectiveDate: 'İstenen yürürlük tarihi',
    effectiveAsap: 'Mümkün olan en kısa sürede',
    effectiveFuture: 'İleri bir tarih',
    effectiveDateField: 'Yürürlük tarihi',
    mailingStatus: 'Şirket posta adresi',
    mailingHave: 'Bir posta adresim var',
    mailingNeed: 'Posta adresi için yardıma ihtiyacım var',
    mailingAddress: 'Posta adresi',
    physicalAddress: 'Asıl fiziksel adres',
    county: 'İlçe (county) ve eyalet',
    registeredAgent: 'Kayıtlı temsilci (registered agent)',
    agentHave: 'Kayıtlı temsilcim var',
    agentNeed: 'Yardıma ihtiyacım var',
    agentNotSure: 'Emin değilim',
    management: 'Yönetim yapısı',
    memberManaged: 'Üye yönetimli',
    managerManaged: 'Müdür yönetimli',
    name1Required: 'En azından birinci şirket adı tercihi zorunludur.',
  },
  parties: {
    heading: 'Sorumlu taraf ve ortaklar',
    rpName: 'Sorumlu taraf adı',
    rpTitle: 'Unvan / şirketle ilişki',
    rpTitleHelp: 'örn. Sahip, Yönetici Üye, Başkan',
    rpAddress: 'Sorumlu taraf adresi',
    taxIdType: 'Vergi kimliği türü',
    taxSsn: 'ABD SSN',
    taxItin: 'ITIN',
    taxForeign: 'Yabancı / yok',
    taxIdValue: 'SSN veya ITIN',
    taxIdHelp: 'Kimlik numaranız şifrelenir ve asla tam olarak geri gösterilmez.',
    foreignNote:
      'Yabancı sorumlu taraf için ABD kimlik numarası gerekmez. EIN çıkarımında başvuru partneri yol gösterir.',
    ownersHeading: 'Ortaklar / üyeler',
    ownersHelp: 'Her ortağı ekleyin. Verilirse ortaklık yüzdeleri toplamı %100 olmalıdır.',
    ownerName: 'Ad soyad',
    ownerAddress: 'Adres',
    ownerPct: 'Ortaklık %',
    addOwner: 'Ortak ekle',
    numberOfMembers: 'Üye sayısı',
    ownershipOver100: 'Toplam ortaklık %100’ü aşıyor. Lütfen yüzdeleri gözden geçirin.',
    rpNameRequired: 'Sorumlu taraf adı zorunludur.',
  },
  ein: {
    heading: 'EIN başvuru bilgileri (Form SS-4)',
    intro: 'Bu yanıtlar başvuru partnerinin EIN başvurusunu hazırlamasına yardımcı olur.',
    reason: 'Başvuru nedeni',
    reasonOptions: {
      started_new_business: 'Yeni bir işletme kuruldu',
      hired_employees: 'Çalışan işe alındı',
      banking: 'Bankacılık amacıyla',
      changed_org_type: 'Kuruluş türü değişti',
      purchased_business: 'Faal bir işletme satın alındı',
      other: 'Diğer',
    },
    dateStarted: 'İşletme başlangıç tarihi',
    dateTbd: 'Kuruluştan sonra belirlenecek',
    closingMonth: 'Muhasebe yılı kapanış ayı',
    employeesHeading: 'Önümüzdeki 12 ayda beklenen çalışan sayısı',
    employeesHelp: 'İlgili kategoride işe alım beklemiyorsanız 0 girin.',
    empAgricultural: 'Tarım',
    empHousehold: 'Ev hizmetleri',
    empOther: 'Diğer',
    form944: 'Yıllık istihdam vergisi yükümlülüğünüz 1.000 $ veya altı mı olacak (Form 944)?',
    form944Help: 'Küçük işverenler için geçerlidir; uygunluğu başvuru partneri teyit eder.',
    firstWages: 'İlk maaşın ödendiği veya ödeneceği tarih',
    principalProducts: 'Ana mal, ürün veya hizmet grubu',
    previousEin: 'Bu tüzel kişi daha önce EIN başvurusu yaptı mı?',
    previousEinNumber: 'Önceki EIN',
    thirdParty: 'Bu başvuru için Pexify’ın başvuru partnerini üçüncü taraf temsilci olarak yetkilendiriyorum',
    thirdPartyHelp:
      'Bu, başvuru partnerinin bu EIN başvurusu hakkında sizin adınıza IRS ile görüşmesine olanak tanır.',
    certification:
      'Verilen bilgilerin bildiğim kadarıyla doğru olduğunu beyan ederim.',
    certificationHelp: 'Başvurunuzu göndermek için gereklidir.',
  },
  review: {
    heading: 'Başvurunuzu gözden geçirin',
    intro: 'Ödemeden önce her şeyin doğru olduğunu teyit edin. Her bölümü düzenleyebilirsiniz.',
    sectionContact: 'İletişim',
    sectionCompany: 'Şirket',
    sectionParties: 'Ortaklık',
    sectionEin: 'EIN / SS-4',
    editSection: 'Düzenle',
    included: 'Dahil olanlar',
    notIncluded: 'Dahil olmayanlar',
    notIncludedBody:
      'Bu hizmet yalnızca idari başvuru koordinasyonunu kapsar. Hukuki veya vergisel danışmanlık, onay garantisi veya garantili bir EIN çıkış tarihi içermez.',
    amountDue: 'Ödenecek tutar',
    priceUnavailable: 'Fiyat ödeme adımında teyit edilecek.',
    mustCertify: 'Devam etmek için lütfen beyanı onaylayın.',
    mustAgree: 'Devam etmek için lütfen temsilciyi yetkilendirin.',
    continueToPayment: 'Ödemeye devam et',
  },
  payment: {
    heading: 'Ödeme',
    intro: 'Başvuruyu göndermek için güvenli ödemenizi tamamlayın.',
    payButton: 'Öde ve Başvuruyu Gönder',
    processing: 'İşleniyor…',
    cardNote: 'Kart bilgileri Stripe tarafından işlenir — Pexify’a asla ulaşmaz.',
    prepareError: 'Ödemeyi başlatamadık. Lütfen tekrar deneyin.',
    confirming: 'Ödemeniz doğrulanıyor…',
    slowTitle: 'Ödemeniz doğrulanıyor',
    slowBody: (ref) =>
      `Bu işlem her zamankinden biraz uzun sürüyor. Ödemeniz iletildi ve başvurunuz (${ref}) kaydedildi — bu sayfayı güvenle kapatabilirsiniz, doğrulamayı e-posta ile bildireceğiz. Tekrar ödeme yapmanıza gerek yok.`,
    successTitle: 'Başvuru gönderildi',
    successBody: (ref) =>
      `Teşekkürler. ${ref} numaralı başvurunuz alındı ve ödeme doğrulandı. Pexify sonraki adımları başvuru partneriyle koordine edecek ve sizi e-posta ile bilgilendirecek.`,
    referenceLabel: 'Referansınız',
    failedTitle: 'Ödeme tamamlanmadı',
    failedBody:
      'Ödemeniz doğrulanamadı. Herhangi bir başvuru gönderilmedi. Lütfen tekrar deneyin veya başka bir kart kullanın.',
  },
  months: [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ],
}

export const START_COMPANY_STRINGS: LocalizedDict<StartCompanyStrings> = {
  en: EN,
  tr: TR,
}

export type { StartCompanyStrings }
